import React, { useState, useEffect } from "react";

const LegalPages = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState("terms"); // "terms" or "privacy"

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const containerStyle = {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
    padding: isMobile ? "1rem" : "2rem 0",
    color: "#e2e8f0"
  };

  const contentStyle = {
    maxWidth: "900px",
    margin: "0 auto",
    background: "rgba(255, 255, 255, 0.02)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "24px",
    padding: isMobile ? "1.5rem" : "3rem",
    boxShadow: "0 25px 50px rgba(0, 0, 0, 0.3)"
  };

  const tabContainerStyle = {
    display: "flex",
    justifyContent: "center",
    marginBottom: "2rem",
    gap: "1rem"
  };

  const tabButtonStyle = (isActive) => ({
    padding: isMobile ? "0.75rem 1.5rem" : "1rem 2rem",
    borderRadius: "12px",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    background: isActive 
      ? "linear-gradient(135deg, #667eea, #764ba2)" 
      : "rgba(255, 255, 255, 0.05)",
    color: "white",
    fontSize: isMobile ? "0.9rem" : "1rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease"
  });

  const headerStyle = {
    textAlign: "center",
    marginBottom: isMobile ? "2rem" : "3rem",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    paddingBottom: "2rem"
  };

  const titleStyle = {
    fontSize: isMobile ? "2rem" : "2.5rem",
    fontWeight: "700",
    background: "linear-gradient(135deg, #667eea, #764ba2)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    marginBottom: "1rem"
  };

  const sectionStyle = {
    marginBottom: isMobile ? "1.5rem" : "2rem"
  };

  const sectionTitleStyle = {
    fontSize: isMobile ? "1.25rem" : "1.4rem",
    fontWeight: "600",
    color: "#68d391",
    marginBottom: "1rem",
    borderLeft: "4px solid #68d391",
    paddingLeft: "1rem"
  };

  const subsectionTitleStyle = {
    fontSize: isMobile ? "1.1rem" : "1.2rem",
    fontWeight: "600",
    color: "#667eea",
    marginBottom: "0.75rem",
    marginTop: "1.5rem"
  };

  const textStyle = {
    lineHeight: "1.6",
    color: "#cbd5e0",
    fontSize: isMobile ? "0.9rem" : "1rem",
    marginBottom: "1rem"
  };

  const listStyle = {
    paddingLeft: "1.5rem",
    marginBottom: "1rem"
  };

  const listItemStyle = {
    marginBottom: "0.5rem",
    color: "#cbd5e0",
    lineHeight: "1.6"
  };

  const highlightStyle = {
    background: "rgba(104, 211, 145, 0.1)",
    border: "1px solid rgba(104, 211, 145, 0.3)",
    borderRadius: "8px",
    padding: "1rem",
    margin: "1rem 0",
    color: "#68d391",
    fontWeight: "500"
  };

  const contactStyle = {
    background: "rgba(102, 126, 234, 0.1)",
    border: "1px solid rgba(102, 126, 234, 0.2)",
    borderRadius: "12px",
    padding: isMobile ? "1rem" : "1.5rem"
  };

  const renderTermsContent = () => (
    <>
      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>CRAFTING BRAIN – TERMS & CONDITIONS</h2>
        <p style={textStyle}>
          <strong>6-Month Internship, Product Development & Placement Assistance Program</strong>
        </p>
        <p style={textStyle}>
          <strong>Last Updated:</strong> 11 / 01 / 2026
        </p>
        <p style={textStyle}>
          By enrolling in, accessing, or participating in the Crafting Brain 6-Month Internship & Placement
          Assistance Program (“Program”), the student/candidate (“Candidate”) expressly agrees to be legally
          bound by the following Terms & Conditions.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>1. Definitions</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong>“Crafting Brain”</strong> refers to Crafting Braincraft AI OPC Pvt Ltd and its representatives.</li>
          <li style={listItemStyle}><strong>“Inikola”</strong> refers to Inikola and its associated platforms, products, and teams.</li>
          <li style={listItemStyle}><strong>“Program”</strong> refers to the 6-month training, internship, product development, and placement assistance offering.</li>
          <li style={listItemStyle}><strong>“Candidate”</strong> refers to any enrolled student, intern, or participant.</li>
          <li style={listItemStyle}><strong>“Placement Pool”</strong> refers to the internal list of candidates eligible for placement assistance.</li>
          <li style={listItemStyle}><strong>“Confidential Information”</strong> includes all non-public technical, business, and operational information.</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>2. Nature of the Program</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>This Program is purely educational and skill-development oriented.</li>
          <li style={listItemStyle}>The Program does <strong>NOT</strong> constitute employment, an offer of employment, or a promise of salary.</li>
          <li style={listItemStyle}>Internship provided is learning-based, not employment-based.</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>3. Eligibility & Admission Rights</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>The Program is open to students, graduates, and working professionals.</li>
          <li style={listItemStyle}>Crafting Brain reserves the absolute right to approve, reject, or revoke enrollment at any stage.</li>
          <li style={listItemStyle}>Misrepresentation of identity, qualifications, or background may lead to immediate termination.</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>4. Fees, Payments & Refund Policy</h2>
        <div style={highlightStyle}>
          <p style={{ ...textStyle, marginBottom: "0.5rem", color: "#68d391" }}>
            <strong>All program fees are strictly non-refundable and non-transferable.</strong>
          </p>
          <ul style={listStyle}>
            <li style={listItemStyle}>This includes: Mid-course dropouts, Internship dropouts, Placement-stage exits, and Disciplinary removals.</li>
            <li style={listItemStyle}>Failure to pay installments on time may result in suspension of access and/or termination without refund.</li>
          </ul>
        </div>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>5. Attendance Policy (Mandatory)</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>Candidates must maintain a minimum of <strong>85% attendance</strong>.</li>
          <li style={listItemStyle}>Attendance includes: Live classes, Recorded session compliance, Assignments, Internship tasks, Reviews and evaluations.</li>
          <li style={listItemStyle}>Attendance manipulation or proxy attendance is a serious violation.</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>6. Performance Evaluation Policy</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>Candidates must maintain an overall performance score <strong>greater than 75%</strong>.</li>
          <li style={listItemStyle}>
            Performance is evaluated using multiple parameters including but not limited to:
            Assignments, Projects, Assessments, Product contributions, Mentor feedback, Professional behavior.
          </li>
          <li style={listItemStyle}>Evaluation parameters are solely defined, modified, and controlled by Crafting Brain.</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>7. Placement Pool Eligibility</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>Placement assistance begins only after <strong>50% of course completion</strong>.</li>
          <li style={listItemStyle}>To enter the Placement Pool, the Candidate must maintain ≥85% attendance and achieve &gt;75% overall performance.</li>
          <li style={listItemStyle}>Entry into the placement pool is conditional, discretionary, and not automatic.</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>8. Placement Assistance Disclaimer</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>Crafting Brain provides placement assistance only, not job guarantees.</li>
          <li style={listItemStyle}>Assistance includes: Resume support, Mock interviews, Interview referrals.</li>
          <li style={listItemStyle}>Final hiring decisions rest entirely with recruiting companies.</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>9. Guaranteed Interview Policy</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>Eligible Placement Pool candidates are guaranteed a maximum of <strong>3 interview opportunities</strong>.</li>
          <li style={listItemStyle}>These interviews do not guarantee selection.</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>10. Interview Attendance & Forfeiture Policy</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>Candidates must attend all scheduled interviews.</li>
          <li style={listItemStyle}>Absence is allowed only once, strictly for medical reasons, with minimum <strong>48-hour prior written notice</strong>.</li>
          <li style={listItemStyle}>Any missed interview without compliance will be counted toward the 3 interviews.</li>
          <li style={listItemStyle}>After one exemption, all future absences are counted regardless of reason.</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>11. Internship Structure & Authority</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>Internship may be Remote, Hybrid, or On-site.</li>
          <li style={listItemStyle}>Internship duration, structure, and evaluations are jointly controlled by Crafting Brain and Inikola.</li>
          <li style={listItemStyle}>Internship tasks may include work on live, confidential products.</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>12. Monthly Stipend Policy</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>Top 10% performers each month may receive a <strong>₹10,000 stipend</strong>.</li>
          <li style={listItemStyle}>Stipend is evaluated monthly, performance-based, and not guaranteed.</li>
          <li style={listItemStyle}>Rankings reset every month.</li>
          <li style={listItemStyle}>Stipend decisions are final and non-negotiable.</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>13. Non-Disclosure Agreement (NDA)</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>Candidates may access highly confidential information including source code, models, architectures, product ideas, and business strategies.</li>
          <li style={listItemStyle}>Candidates agree not to disclose, share, reproduce, or misuse confidential information.</li>
          <li style={listItemStyle}>Candidates agree not to claim ownership over internship outputs.</li>
          <li style={listItemStyle}>NDA obligations survive completion, termination, or dropout.</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>14. Intellectual Property (IP) Ownership</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            All work created during the Program (including code, models, data, designs, documentation) shall be the
            exclusive intellectual property of Crafting Brain and/or Inikola.
          </li>
          <li style={listItemStyle}>Candidates waive all IP claims.</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>15. Mid-Course / Internship Dropout Policy</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>Any dropout at any stage results in no refund and loss of all benefits.</li>
          <li style={listItemStyle}>Crafting Brain reserves full authority to define dropout cases.</li>
          <li style={listItemStyle}>Decisions are final and binding.</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>16. Code of Conduct</h2>
        <p style={textStyle}>Candidates must:</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>Maintain professionalism.</li>
          <li style={listItemStyle}>Avoid plagiarism, misconduct, harassment, or misuse.</li>
          <li style={listItemStyle}>Respect confidentiality and IP.</li>
        </ul>
        <p style={textStyle}>Violation may lead to immediate termination without refund.</p>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>17. Certification Policy</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>Certificates are issued only upon completion of course and meeting attendance & performance criteria.</li>
          <li style={listItemStyle}>No certificates for incomplete participation.</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>18. Program Modifications</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>Crafting Brain may modify curriculum, tools, trainers, and schedules.</li>
          <li style={listItemStyle}>No refunds apply for modifications.</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>19. Limitation of Liability</h2>
        <p style={textStyle}>Crafting Brain is not liable for:</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>Hiring outcomes.</li>
          <li style={listItemStyle}>Salary packages.</li>
          <li style={listItemStyle}>Internet or device issues.</li>
          <li style={listItemStyle}>Personal constraints.</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>20. Termination Rights</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>Crafting Brain may terminate enrollment for rule violations, non-payment, or misconduct.</li>
          <li style={listItemStyle}>No refunds apply.</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>21. Governing Law & Jurisdiction</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>Governed by Indian Law.</li>
          <li style={listItemStyle}>Jurisdiction: Hyderabad, Telangana.</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>22. Final Authority Clause</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>Crafting Brain’s interpretation, decisions, and actions are final, binding, and non-negotiable.</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>23. Acceptance</h2>
        <p style={textStyle}>By enrolling, the Candidate confirms:</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>Full understanding.</li>
          <li style={listItemStyle}>Voluntary acceptance.</li>
          <li style={listItemStyle}>Legal consent to all terms.</li>
        </ul>
      </div>
    </>
  );

  const renderPrivacyContent = () => (
    <>
      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>1. Introduction</h2>
        <p style={textStyle}>
          Crafting Brain is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, register for courses, or use our services.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>2. Information We Collect</h2>
        
        <h3 style={subsectionTitleStyle}>2.1 Personal Information</h3>
        <p style={textStyle}>
          We collect personal information that you voluntarily provide when you:
        </p>
        <ul style={listStyle}>
          <li style={listItemStyle}>Register for an account or course</li>
          <li style={listItemStyle}>Make a payment</li>
          <li style={listItemStyle}>Contact our support team</li>
          <li style={listItemStyle}>Subscribe to communications</li>
        </ul>

        <div style={highlightStyle}>
          <p style={{...textStyle, marginBottom: "0.5rem"}}><strong>Types of information collected:</strong></p>
          <ul style={listStyle}>
            <li style={listItemStyle}>Full name and contact information</li>
            <li style={listItemStyle}>Email address and phone number</li>
            <li style={listItemStyle}>Payment information (securely processed)</li>
            <li style={listItemStyle}>Educational background (optional)</li>
          </ul>
        </div>

        <h3 style={subsectionTitleStyle}>2.2 Automatically Collected Information</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>IP address and location data</li>
          <li style={listItemStyle}>Browser type and device information</li>
          <li style={listItemStyle}>Pages viewed and time spent</li>
          <li style={listItemStyle}>Cookies and tracking technologies</li>
        </ul>

        <h3 style={subsectionTitleStyle}>2.3 Course Activity Data</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>Course progress and completion status</li>
          <li style={listItemStyle}>Assignment submissions and grades</li>
          <li style={listItemStyle}>Video engagement metrics</li>
          <li style={listItemStyle}>Quiz and assessment results</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>3. How We Use Your Information</h2>
        
        <h3 style={subsectionTitleStyle}>3.1 Primary Uses</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>Process course registrations and payments</li>
          <li style={listItemStyle}>Provide access to course materials</li>
          <li style={listItemStyle}>Deliver customer support</li>
          <li style={listItemStyle}>Send course-related communications</li>
          <li style={listItemStyle}>Track progress and issue certificates</li>
        </ul>

        <h3 style={subsectionTitleStyle}>3.2 Secondary Uses (with consent)</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>Send promotional emails about new courses</li>
          <li style={listItemStyle}>Conduct market research</li>
          <li style={listItemStyle}>Personalize learning experience</li>
          <li style={listItemStyle}>Provide career guidance</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>4. Information Sharing</h2>
        
        <h3 style={subsectionTitleStyle}>4.1 Third-Party Service Providers</h3>
        <p style={textStyle}>
          We may share information with trusted providers for:
        </p>
        <ul style={listStyle}>
          <li style={listItemStyle}>Payment processing (Razorpay, PayU)</li>
          <li style={listItemStyle}>Email communication services</li>
          <li style={listItemStyle}>Cloud storage and hosting</li>
          <li style={listItemStyle}>Analytics and security services</li>
        </ul>

        <div style={highlightStyle}>
          <p style={{...textStyle, marginBottom: 0}}>
            <strong>All third-party providers are contractually obligated to protect your information.</strong>
          </p>
        </div>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>5. Data Security</h2>
        
        <h3 style={subsectionTitleStyle}>5.1 Security Measures</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>SSL/TLS encryption for data transmission</li>
          <li style={listItemStyle}>Secure storage with encryption at rest</li>
          <li style={listItemStyle}>Regular security audits</li>
          <li style={listItemStyle}>Access controls and authentication</li>
        </ul>

        <h3 style={subsectionTitleStyle}>5.2 Payment Security</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>No storage of complete credit card information</li>
          <li style={listItemStyle}>PCI-compliant payment processors</li>
          <li style={listItemStyle}>Fraud detection systems</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>6. Your Privacy Rights</h2>
        
        <h3 style={subsectionTitleStyle}>6.1 Access and Control</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>Access your personal information</li>
          <li style={listItemStyle}>Update or correct your data</li>
          <li style={listItemStyle}>Request deletion of your account</li>
          <li style={listItemStyle}>Opt out of marketing communications</li>
        </ul>

        <h3 style={subsectionTitleStyle}>6.2 Cookie Management</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>Control settings through browser preferences</li>
          <li style={listItemStyle}>Use our cookie consent options</li>
          <li style={listItemStyle}>Opt out of tracking services</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>7. Data Retention</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong>Account Information:</strong> Account duration plus 7 years</li>
          <li style={listItemStyle}><strong>Course Data:</strong> 5 years for certificate verification</li>
          <li style={listItemStyle}><strong>Payment Records:</strong> 7 years for accounting</li>
          <li style={listItemStyle}><strong>Marketing Data:</strong> Until opt-out or 3 years inactivity</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>8. International Transfers</h2>
        <p style={textStyle}>
          Your information may be processed internationally with appropriate safeguards including standard contractual clauses and compliance with data protection laws.
        </p>
      </div>
    </>
  );

  return (
    <div style={containerStyle}>
      <div style={contentStyle}>
        {/* Tab Navigation */}
        <div style={tabContainerStyle}>
          <button
            style={tabButtonStyle(activeTab === "terms")}
            onClick={() => setActiveTab("terms")}
            onMouseOver={(e) => {
              if (activeTab !== "terms") {
                e.target.style.background = "rgba(255, 255, 255, 0.1)";
              }
            }}
            onMouseOut={(e) => {
              if (activeTab !== "terms") {
                e.target.style.background = "rgba(255, 255, 255, 0.05)";
              }
            }}
          >
            Terms & Conditions
          </button>
          <button
            style={tabButtonStyle(activeTab === "privacy")}
            onClick={() => setActiveTab("privacy")}
            onMouseOver={(e) => {
              if (activeTab !== "privacy") {
                e.target.style.background = "rgba(255, 255, 255, 0.1)";
              }
            }}
            onMouseOut={(e) => {
              if (activeTab !== "privacy") {
                e.target.style.background = "rgba(255, 255, 255, 0.05)";
              }
            }}
          >
            Privacy Policy
          </button>
        </div>

        {/* Header */}
        <div style={headerStyle}>
          <h1 style={titleStyle}>
            {activeTab === "terms" ? "Terms and Conditions" : "Privacy Policy"}
          </h1>
        </div>

        {/* Content */}
        {activeTab === "terms" ? renderTermsContent() : renderPrivacyContent()}

        {/* Contact Information */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Contact Information</h2>
          <div style={contactStyle}>
            <p style={textStyle}><strong>Crafting Brain</strong></p>
            <p style={textStyle}>Email: support@craftingbrain.com</p>
            <p style={textStyle}>Phone: +91 93050 09726</p>
            <p style={textStyle}>Address: [Your Business Address]</p>
          </div>
        </div>

        {/* Footer Note */}
        <div style={{
          marginTop: "3rem",
          padding: "1.5rem",
          background: "rgba(255, 255, 255, 0.05)",
          borderRadius: "12px",
          textAlign: "center",
          border: "1px solid rgba(255, 255, 255, 0.1)"
        }}>
          <p style={{ ...textStyle, fontStyle: "italic", marginBottom: 0 }}>
            {activeTab === "terms" 
              ? "By proceeding with course registration and payment, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions."
              : "This Privacy Policy is designed to be transparent about our data practices. If you have any questions or concerns, please don't hesitate to contact us."
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default LegalPages;
